/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Grid } from '@/components/Grid';
import NextImage, { blurDataURLPlaceholder } from '@/components/NextImage';
import { useLabels } from '@/hooks/useLabels';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Actionable, Text, View } from '@az/starc-ui';
import {
  useProductReviewsImages,
  type ExtendedProductReviewImage,
} from '../../hooks/useProductReviewsImages';
import { type ActiveImage } from '../../hooks/useReviewImageModal';
import styles from './styles.module.scss';

const labelMap = {
  customerImages: 'label_reviews_customerImages',
  seeAllImages: 'label_reviews_seeAllImages',
  placeholderImage: 'label_reviews_placeholderImage',
  customerImageAltFallback: 'label_reviews_customerImageAltFallback',
};

const placeholderImages: ExtendedProductReviewImage[] = Array.from({ length: 6 }).map((_, i) => ({
  id: `${i}`,
  reviewID: '0',
  normalPhotoUrl: blurDataURLPlaceholder,
  thumbPhotoUrl: '',
  photoCaption: '',
}));

export type CustomerImagesProps = {
  onSeeAllClick: () => void;
  onImageClick: (image: ActiveImage) => void;
};

export function CustomerImages({ onSeeAllClick, onImageClick }: CustomerImagesProps) {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const labels = useLabels(labelMap);
  const { images } = useProductReviewsImages();

  const maxImageCount = isDesktop ? 6 : 4;
  const gridImages = images ?? placeholderImages;

  return (
    <View>
      <View
        direction="row"
        align="center"
        justify="space-between"
        className={styles.customerImagesHeader}
      >
        <View>
          <Text className={styles.sectionTitle} weight="bold">
            {labels.customerImages}
          </Text>
        </View>
        <View>
          <Actionable disabled={!images?.length} onClick={() => onSeeAllClick()}>
            <Text className={styles.allImages} decoration="underline">
              {labels.seeAllImages}
            </Text>
          </Actionable>
        </View>
      </View>

      <View>
        <Grid container>
          {gridImages.slice(0, maxImageCount).map((image) => (
            <Grid item key={image.id} sm={3} md={3} lg={2}>
              <Actionable
                disabled={image.reviewID === '0'}
                className={styles.imageButton}
                onClick={() => {
                  if (images !== undefined) {
                    onImageClick({ imageId: image.id, reviewId: image.reviewID });
                  }
                }}
              >
                <NextImage
                  width={200}
                  height={200}
                  src={image.normalPhotoUrl}
                  className={styles.reviewImage}
                  blurDataURL={blurDataURLPlaceholder}
                  alt={image.photoCaption ?? labels.customerImageAltFallback}
                />
              </Actionable>
            </Grid>
          ))}
          {gridImages.length === 0 && (
            <Grid item key="placeholder" sm={3} md={3} lg={2}>
              <View className={styles.imageButton}>
                <NextImage
                  width={200}
                  height={200}
                  src={labels.placeholderImage}
                  className={styles.reviewImage}
                  blurDataURL={blurDataURLPlaceholder}
                />
              </View>
            </Grid>
          )}
        </Grid>
      </View>
    </View>
  );
}
